// Libs
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import PaperAirplaneIcon from "@heroicons/react/24/outline/PaperAirplaneIcon";
import { User } from "firebase/auth";

// Local
import HeadingOne from "../../components/HeadingOne";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import SafeEmailPreview from "../../components/EmailMarketing/SafeEmailPreview";
import SendTestEmailDialog from "../../components/EmailMarketing/SendTestEmailDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import {
  ExistingEmailTemplate,
  EmailTemplateStatus,
} from "../../models/email-template";
import { useAuthStore } from "../../store/firebase-auth";
import {
  EMAIL_TEMPLATES_CREATE_URL,
  EMAIL_TEMPLATES_EDIT_URL,
  EMAIL_MARKETING_URL,
} from "../../urls";
import { DbRead } from "../../database";

interface Props {
  siteKey: string;
}

export default function EmailTemplatesListContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  const [templates, setTemplates] = useState<ExistingEmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    templateId: string;
    templateName: string;
  }>({
    isOpen: false,
    templateId: "",
    templateName: "",
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [testEmailDialog, setTestEmailDialog] = useState<{
    isOpen: boolean;
    templateId: string;
    templateName: string;
  }>({
    isOpen: false,
    templateId: "",
    templateName: "",
  });

  // Sort templates by most recent first
  const sortedTemplates = useMemo(
    () =>
      [...templates].sort(
        (a, b) =>
          b.timestampLastModified.toMillis() -
          a.timestampLastModified.toMillis(),
      ),
    [templates],
  );

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  useEffect(() => {
    if (!siteKey) {
      setLoading(false);
      return;
    }

    // Subscribe to real-time updates of email templates
    const unsubscribe = DbRead.emailTemplates.subscribe({
      siteKey,
      onChange: (fetchedTemplates) => {
        setTemplates(fetchedTemplates);
        setLoading(false);
        setError(null);
        // Auto-select first template if none selected
        if (fetchedTemplates.length > 0 && !selectedTemplateId) {
          const sortedFetched = [...fetchedTemplates].sort(
            (a, b) =>
              b.timestampLastModified.toMillis() -
              a.timestampLastModified.toMillis(),
          );
          setSelectedTemplateId(sortedFetched[0].id);
        }
      },
      onError: (error) => {
        console.error("Error fetching templates:", error);
        setError(error.message || "Failed to load templates");
        setLoading(false);
      },
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [siteKey, selectedTemplateId]);

  const handleCreateTemplate = () => {
    navigate(EMAIL_TEMPLATES_CREATE_URL);
  };

  const handleEditTemplate = (templateId: string) => {
    navigate(`${EMAIL_TEMPLATES_EDIT_URL}/${templateId}`);
  };

  const handleBackToEmailMarketing = () => {
    navigate(EMAIL_MARKETING_URL);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const handleDeleteTemplate = (templateId: string, templateName: string) => {
    setDeleteDialog({
      isOpen: true,
      templateId,
      templateName,
    });
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteDialog.templateId) return;

    setIsDeleting(true);
    try {
      await DbRead.emailTemplates.delete({
        siteKey,
        templateID: deleteDialog.templateId,
      });
      // If we deleted the selected template, clear selection
      if (deleteDialog.templateId === selectedTemplateId) {
        setSelectedTemplateId("");
      }
      // The subscription will automatically update the list
    } catch (error) {
      console.error("Error deleting template:", error);
      setError(
        error instanceof Error ? error.message : "Failed to delete template",
      );
    } finally {
      setIsDeleting(false);
      setDeleteDialog({ isOpen: false, templateId: "", templateName: "" });
    }
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setDeleteDialog({ isOpen: false, templateId: "", templateName: "" });
    }
  };

  const handleSendTestEmail = (templateId: string, templateName: string) => {
    setTestEmailDialog({
      isOpen: true,
      templateId,
      templateName,
    });
  };

  const handleCloseTestEmailDialog = () => {
    setTestEmailDialog({ isOpen: false, templateId: "", templateName: "" });
  };

  const getStatusBadgeColor = (status: EmailTemplateStatus) => {
    switch (status) {
      case EmailTemplateStatus.ACTIVE:
        return "bg-green-100 text-green-800";
      case EmailTemplateStatus.DRAFT:
        return "bg-yellow-100 text-yellow-800";
      case EmailTemplateStatus.ARCHIVED:
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    return timestamp.toDate().toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Error loading templates: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-red-600 underline hover:text-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <BaseButtonSecondary
            onClick={handleBackToEmailMarketing}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Email Marketing
          </BaseButtonSecondary>
          <HeadingOne>Email Templates</HeadingOne>
        </div>
        <BaseButtonPrimary
          onClick={handleCreateTemplate}
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Create Template
        </BaseButtonPrimary>
      </div>

      {/* Helper Text */}
      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h3 className="mb-2 text-sm font-medium text-blue-900">
          What are Email Templates?
        </h3>
        <p className="text-sm text-blue-800">
          Email templates are reusable email designs that you can customize with
          your content, branding, and messaging. Once you create a template, you
          can use it to launch email campaigns by selecting which customers
          should receive it. Templates help you maintain consistent branding and
          save time when sending emails to your customers.
        </p>
      </div>

      {templates.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mx-auto h-24 w-24 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-8 5.25L3 5"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No email templates
          </h3>
          <p className="mt-2 text-gray-500">
            Get started by creating your first email template.
          </p>
          <div className="mt-6">
            <BaseButtonPrimary
              onClick={handleCreateTemplate}
              className="flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Create Your First Template
            </BaseButtonPrimary>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Template List - Left Side */}
          <div className="col-span-5">
            <h3 className="mb-3 text-sm font-medium text-gray-900">
              Templates
            </h3>
            <div className="max-h-[600px] space-y-0 overflow-y-auto rounded-lg border border-gray-200">
              {sortedTemplates.map((template) => (
                <div
                  key={template.id}
                  className={`cursor-pointer border-b border-gray-100 p-3 transition-all last:border-b-0 ${
                    selectedTemplateId === template.id
                      ? "bg-primary/5 border-l-4 border-l-primary"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="mr-3 min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <h4 className="truncate text-sm font-medium text-gray-900">
                          {template.name}
                        </h4>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeColor(template.status)}`}
                        >
                          {template.status}
                        </span>
                      </div>
                      <p className="mb-1 truncate text-xs text-gray-600">
                        {template.subject}
                      </p>
                      <div className="text-xs text-gray-500">
                        <span>
                          {formatDate(template.timestampLastModified)}
                        </span>
                      </div>
                    </div>

                    {/* Action icons */}
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendTestEmail(template.id, template.name);
                        }}
                        className="rounded p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-600"
                        title="Send test email"
                      >
                        <PaperAirplaneIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTemplate(template.id);
                        }}
                        className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                        title="Edit template"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTemplate(template.id, template.name);
                        }}
                        className="rounded p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600"
                        title="Delete template"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Template Preview - Right Side */}
          <div className="col-span-7">
            <h3 className="mb-3 text-sm font-medium text-gray-900">Preview</h3>
            {selectedTemplate ? (
              <div className="rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {selectedTemplate.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedTemplate.subject}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <BaseButtonSecondary
                        onClick={() =>
                          handleSendTestEmail(
                            selectedTemplate.id,
                            selectedTemplate.name,
                          )
                        }
                        className="flex items-center gap-2"
                      >
                        <PaperAirplaneIcon className="h-4 w-4" />
                        Test Email
                      </BaseButtonSecondary>
                      <BaseButtonPrimary
                        onClick={() => handleEditTemplate(selectedTemplate.id)}
                        className="flex items-center gap-2"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </BaseButtonPrimary>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="mb-3 border-b border-gray-200 pb-3">
                    <div className="mb-1 text-xs font-medium text-gray-500">
                      Subject:
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {selectedTemplate.subject}
                    </div>
                  </div>
                  {selectedTemplate.description && (
                    <div className="mb-3 border-b border-gray-200 pb-3">
                      <div className="mb-1 text-xs font-medium text-gray-500">
                        Description:
                      </div>
                      <div className="text-sm text-gray-700">
                        {selectedTemplate.description}
                      </div>
                    </div>
                  )}
                  <div className="max-h-96 overflow-y-auto">
                    <div className="mb-2 text-xs font-medium text-gray-500">
                      Content:
                    </div>
                    <SafeEmailPreview
                      htmlContent={selectedTemplate.htmlContent}
                      className="w-full rounded border border-gray-200"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h4 className="mt-2 text-sm font-medium text-gray-900">
                  Select a template
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  Choose a template from the list to see a preview here.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleCloseDeleteDialog}
        handleConfirmAction={confirmDeleteTemplate}
        isSubmitting={isDeleting}
        title="Delete Email Template"
        body={
          <div>
            <p className="mb-4">
              Are you sure you want to delete the template{" "}
              <strong>"{deleteDialog.templateName}"</strong>?
            </p>
            <p className="text-sm text-gray-600">
              This action cannot be undone. Any existing campaigns using this
              template will not be affected.
            </p>
          </div>
        }
      />

      {/* Send Test Email Dialog */}
      <SendTestEmailDialog
        isOpen={testEmailDialog.isOpen}
        onClose={handleCloseTestEmailDialog}
        templateId={testEmailDialog.templateId}
        templateName={testEmailDialog.templateName}
        siteKey={siteKey}
      />
    </div>
  );
}
